import React from 'react'
import { usePullToRefresh } from '@/hooks/use-pull-to-refresh'
import { useEnhancedMobile } from '@/hooks/use-enhanced-mobile'
import { RefreshCw } from 'lucide-react'
import { cn } from '@/lib/utils'

interface MobileGestureContainerProps {
  children: React.ReactNode
  onRefresh?: () => Promise<void> | void
}

export const MobileGestureContainer: React.FC<MobileGestureContainerProps> = ({ 
  children, 
  onRefresh 
}) => {
  const { isMobile } = useEnhancedMobile()

  const { isRefreshing, pullDistance, shouldTriggerRefresh } = usePullToRefresh({
    onRefresh: onRefresh || (() => Promise.resolve()),
    enabled: Boolean(onRefresh) && isMobile
  })

  if (!isMobile) {
    return <>{children}</>
  }

  return (
    <div className="relative min-h-screen">
      {/* Pull to refresh indicator */}
      {onRefresh && (
        <div 
          className={cn(
            "fixed top-0 left-0 right-0 z-40 flex items-center justify-center",
            "bg-background/90 backdrop-blur-sm transition-all duration-300",
            pullDistance > 0 ? "opacity-100" : "opacity-0"
          )}
          style={{ 
            height: Math.min(pullDistance, 80),
            transform: `translateY(${Math.max(0, pullDistance - 80)}px)`
          }}
        >
          <RefreshCw 
            size={24} 
            className={cn(
              "text-primary transition-transform duration-300",
              isRefreshing && "animate-spin",
              shouldTriggerRefresh && !isRefreshing && "rotate-180"
            )}
          />
        </div>
      )}

      {/* Content with pull distance offset */}
      <div 
        style={{ 
          transform: onRefresh && pullDistance > 0 
            ? `translateY(${Math.min(pullDistance * 0.5, 40)}px)` 
            : undefined,
          transition: pullDistance === 0 ? 'transform 0.3s ease-out' : 'none'
        }}
      >
        {children}
      </div>
    </div>
  )
}
